import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { initCheckoutSession, trackMixpanel } from '@monorepo/client-common';
import {
  CheckoutEntryPath,
  CheckoutSource,
  EventTypes,
  OptionalAssets,
  SupportedCurrencies,
} from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';
import { useStore } from '@hub/helpers/use-store';
import { QueryConfigKeys } from '@hub/utils/react-query/query-config';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { useQueryClient } from '@tanstack/react-query';
import { useQuickPurchaseParams } from './use-quick-purchase-params';

export enum QuickPurchaseFlowStep {
  Validating = 'validating',
  AuthCheck = 'auth_check',
  ModalEmail = 'modal_email',
  ModalOtp = 'modal_otp',
  ModalCountry = 'modal_country',
  CreatingAnon = 'creating_anon',
  PreparingCart = 'preparing_cart',
  Redirecting = 'redirecting',
  Error = 'error',
}

export enum QuickPurchaseError {
  ProgramNotFound = 'PROGRAM_NOT_FOUND',
  ProgramNotSellable = 'PROGRAM_NOT_SELLABLE',
  AssetResolutionFailed = 'ASSET_RESOLUTION_FAILED',
  CartFailed = 'CART_FAILED',
  CartPurchaseLimit = 'CART_PROGRAM_PURCHASE_LIMIT',
  CartCountryExcluded = 'CART_PROGRAM_COUNTRY_EXCLUDED',
  ExistingUserMissingProfile = 'EXISTING_USER_MISSING_PROFILE',
  OtpEmailMismatch = 'OTP_EMAIL_MISMATCH',
  Generic = 'GENERIC',
}

interface QuickPurchaseFlowState {
  step: QuickPurchaseFlowStep;
  error: QuickPurchaseError | null;
  asset: OptionalAssets | null;
  currency: SupportedCurrencies | null;
}

/**
 * Orchestration hook for the Quick Purchase flow.
 * Handles param validation, auth check, modal state, backend calls, and redirect.
 */
export const useQuickPurchaseFlow = () => {
  const { programId, currency, isValid } = useQuickPurchaseParams();
  const {
    dataStore: { userStore },
  } = useStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [state, setState] = useState<QuickPurchaseFlowState>({
    step: QuickPurchaseFlowStep.Validating,
    error: null,
    asset: null,
    currency: null,
  });

  const hasInitialized = useRef(false);

  const setError = useCallback((error: QuickPurchaseError) => {
    setState((prev) => ({ ...prev, step: QuickPurchaseFlowStep.Error, error }));
    trackMixpanel({
      event: EventTypes.QUICK_PURCHASE_ERROR,
      properties: {
        checkout_source: CheckoutSource.QuickPurchase,
        error_type: error,
      },
    });
  }, []);

  const initFlow = useCallback(
    async (postAuthUserId?: string) => {
      if (!isValid) {
        setError(QuickPurchaseError.ProgramNotFound);
        return;
      }

      setState((prev) => ({ ...prev, step: QuickPurchaseFlowStep.AuthCheck }));

      const isAuthenticated = !!(postAuthUserId || userStore.isLoggedIn);

      try {
        if (isAuthenticated) {
          setState((prev) => ({
            ...prev,
            step: QuickPurchaseFlowStep.PreparingCart,
          }));
        }

        const result = await getEnv().apiFactory.quickPurchaseService.init(
          programId ?? '',
          currency ?? undefined,
        );

        initCheckoutSession(
          CheckoutEntryPath.NewOrder,
          CheckoutSource.QuickPurchase,
        );
        trackMixpanel({
          event: EventTypes.CHECKOUT_ORDER_CLICKED,
          properties: { checkout_source: CheckoutSource.QuickPurchase },
        });

        if (isAuthenticated) {
          // Cart prep is authenticated — userId comes from the session cookie,
          // never from the request body.
          const { cart } =
            await getEnv().apiFactory.quickPurchaseService.prepareCart(
              programId ?? '',
              currency ?? undefined,
            );

          // Seed the React Query cache with the freshly-prepared cart to avoid
          // a stale HTTP 304 hit when the billing page loads GET /cart.
          if (cart) {
            queryClient.setQueryData(
              QUERY_KEYS[QueryConfigKeys.CART].DETAILS,
              cart,
            );
          }

          trackMixpanel({
            event: EventTypes.QUICK_PURCHASE_CART_READY,
            properties: { checkout_source: CheckoutSource.QuickPurchase },
          });

          // Set 'redirecting' — navigation is handled by the effect below.
          setState((prev) => ({
            ...prev,
            asset: result.asset,
            currency: result.currency,
            step: QuickPurchaseFlowStep.Redirecting,
          }));
        } else {
          // Unauthenticated: program validated — open email/OTP modal
          setState((prev) => ({
            ...prev,
            asset: result.asset,
            currency: result.currency,
            step: QuickPurchaseFlowStep.ModalEmail,
          }));
        }
      } catch (err) {
        const message = (err as Error)?.message || '';
        if (message.includes('PROGRAM_NOT_FOUND')) {
          setError(QuickPurchaseError.ProgramNotFound);
        } else if (message.includes('PROGRAM_NOT_SELLABLE')) {
          setError(QuickPurchaseError.ProgramNotSellable);
        } else if (message.includes('ASSET_RESOLUTION_FAILED')) {
          setError(QuickPurchaseError.AssetResolutionFailed);
        } else if (message.includes('CART_PROGRAM_PURCHASE_LIMIT')) {
          setError(QuickPurchaseError.CartPurchaseLimit);
        } else if (message.includes('CART_PROGRAM_COUNTRY_EXCLUDED')) {
          setError(QuickPurchaseError.CartCountryExcluded);
        } else if (isAuthenticated) {
          // Any unrecognized error after auth means cart preparation failed
          setError(QuickPurchaseError.CartFailed);
        } else {
          setError(QuickPurchaseError.Generic);
        }
      }
    },
    [isValid, programId, currency, userStore, setError, queryClient],
  );

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    initFlow();
  }, [initFlow]);

  // Navigate in a React effect so it always uses the current router context,
  // avoiding stale references after a router swap mid-async flow.
  useEffect(() => {
    if (state.step === QuickPurchaseFlowStep.Redirecting && state.asset) {
      navigate(
        `/purchase?step=billing&asset=${state.asset}&source=quick_purchase`,
        { replace: true },
      );
    }
  }, [state.step, state.asset, navigate]);

  /**
   * Called after OTP/anonymous-user authentication completes.
   * Bypasses the hasInitialized guard so cart preparation runs with the
   * newly-authenticated userId without requiring a page reload.
   */
  const reinitFlow = useCallback(
    (userId: string) => {
      initFlow(userId);
    },
    [initFlow],
  );

  const handleError = useCallback(
    (error: QuickPurchaseError) => {
      setError(error);
    },
    [setError],
  );

  return {
    step: state.step,
    error: state.error,
    handleError,
    reinitFlow,
  };
};
